import { LoggingLevel } from '@augloop/interfaces';

const separator = '|';
const boolPrefix = 'b~';
const boolTrue = 'b~true';
const numberPrefix = 'n~';
const escapeRegex = /\|\~/g;
const escapeSubstitute = '_';

class Bucket {
    public traceLevel: LoggingLevel;
    public count: number = 0;
    public measureSums: Map<string, number> = new Map();
}

export interface ILoggingAggregator {
    readonly eventName: string;
    init: (log: (obj: unknown, isAggregated: boolean) => void) => void;
    add: (obj: unknown) => void;
    flush: (stop: boolean) => void;
}

export class LoggingAggregator implements ILoggingAggregator {
    public readonly eventName: string;

    private condition: (obj: any) => boolean;
    private readonly dimensions: string[];
    private readonly avgMeasures: string[];
    private readonly buckets: Map<string, Bucket> = new Map();
    private log: (obj: unknown, isAggregated: boolean) => void;
    private readonly interval: any;

    public constructor(eventName: string, condition: (obj: any) => boolean, dimensions: string[], avgMeasures: string[], intervalSec: number) {
        this.eventName = eventName;
        this.condition = condition;
        this.dimensions = dimensions;
        this.avgMeasures = avgMeasures;
        if (intervalSec > 0) {
            this.interval = setInterval(this.flush, intervalSec * 1000);
        }
        // If no interval is set, caller must flush manually
    }

    public init = (log: (obj: unknown, isAggregated: boolean) => void): void => {
        this.log = log;
    }

    /* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
    public add = (obj: any): void => {
        if (!this.condition || !this.condition(obj)) {
            this.log(obj, false/*isAggregated*/);
            return;
        }

        const bucketParams = [];
        for (const dimension of this.dimensions) {
            if (obj[dimension] === undefined) {
                bucketParams.push(null);
            } else {
                let prefix = '';
                if (typeof obj[dimension] == 'boolean') {
                    prefix = boolPrefix;
                } else if (typeof obj[dimension] == 'number') {
                    prefix = numberPrefix;
                }
                bucketParams.push(`${prefix}${obj[dimension].toString()
                    .replace(escapeRegex, escapeSubstitute)}`);
            }
        }

        const bucketKey = bucketParams.join(separator);
        let bucket = this.buckets.get(bucketKey);
        if (!bucket) {
            bucket = new Bucket();
            bucket.traceLevel = obj.traceLevel;
            for (const avgMeasure of this.avgMeasures) {
                bucket.measureSums.set(avgMeasure, 0);
            }
            this.buckets.set(bucketKey, bucket);
        }

        bucket.count++;
        for (const avgMeasure of this.avgMeasures) {
            if (obj[avgMeasure]) {
                bucket.measureSums.set(avgMeasure, bucket.measureSums.get(avgMeasure) + obj[avgMeasure]);
            }
        }
    }

    public flush = (stop: boolean = false): void => {
        this.buckets.forEach((bucket, bucketKey) => {
            const obj: any = {
                traceLevel: bucket.traceLevel,
                eventName: this.eventName,
                count: bucket.count
            };

            const bucketParams = bucketKey.split(separator);
            for (let i = 0; i < this.dimensions.length; i++) {
                const dimension = this.dimensions[i];
                const dimensionValue = bucketParams[i];
                if (dimensionValue.indexOf(boolPrefix) === 0) {
                    obj[dimension] = (dimensionValue === boolTrue);
                } else if (dimensionValue.indexOf(numberPrefix) === 0) {
                    obj[dimension] = parseInt(dimensionValue.slice(numberPrefix.length), 10); // Tracelogger only supports ints for now...
                } else {
                    obj[dimension] = dimensionValue;
                }
            }

            for (const avgMeasure of this.avgMeasures) {
                obj[avgMeasure] = Math.round(bucket.measureSums.get(avgMeasure) / bucket.count); // Tracelogger only supports ints for now...
            }

            this.log(obj, true/*isAggregated*/);
        });

        this.buckets.clear();

        if (stop) {
            this.condition = null;
            clearInterval(this.interval);
        }
    }
}
